import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { AreaTexto, Campo, Entrada } from '@/components/ui/Campo'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { cn } from '@/lib/cn'
import {
  COLORES_PILAR,
  pilarEntrada,
  type PilarAdmin,
  type PilarEntrada,
} from '@/lib/schemas/admin'
import { useGuardarPilar } from './api'
import { useFormulario } from './formulario'
import { ModalMarco } from './ModalMarco'

interface ModalPilarProps {
  abierto: boolean
  alCerrar: () => void
  proyectoId: string
  /** Sin pilar: alta. */
  pilar: PilarAdmin | null
}

const NOMBRE_COLOR: Record<number, string> = {
  1: 'Coral',
  2: 'Amarillo',
  3: 'Verde',
  4: 'Azul',
  5: 'Violeta',
  6: 'Naranja',
  7: 'Verde agua',
  8: 'Gris',
}

/** C8 · Nuevo / editar pilar: nombre único, uno de ocho colores, ≤ 140. */
export function ModalPilar(props: ModalPilarProps) {
  if (!props.abierto) return null
  return <Formulario key={props.pilar?.id ?? 'nuevo'} {...props} />
}

function Formulario({ abierto, alCerrar, proyectoId, pilar }: ModalPilarProps) {
  const guardar = useGuardarPilar()
  const avisar = useAvisar()
  const [valores, setValores] = useState<PilarEntrada>(() =>
    pilar ? desde(pilar) : vacio(proyectoId),
  )
  const form = useFormulario(
    pilarEntrada,
    async (_datos, crudos) => {
      await guardar.mutateAsync({ id: pilar?.id ?? null, datos: crudos })
      avisar(pilar ? 'Pilar guardado' : 'Pilar creado')
    },
    alCerrar,
  )

  const descripcion = valores.descripcion ?? ''

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={pilar ? 'Editar pilar' : 'Nuevo pilar'}
      guardar="Guardar"
      ocupado={form.ocupado}
      error={form.errorGeneral}
      alCambiar={form.limpiar}
      alEnviar={() => {
        form.validarYEnviar(valores)
      }}
    >
      <Campo etiqueta="Nombre" ayuda="Único en el proyecto." error={form.errores.nombre}>
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={valores.nombre}
            onChange={(e) => {
              setValores((a) => ({ ...a, nombre: e.target.value }))
            }}
            autoFocus
            maxLength={60}
          />
        )}
      </Campo>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Color</legend>
        <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Color">
          {COLORES_PILAR.map((c) => {
            const elegido = valores.color === c
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={elegido}
                aria-label={NOMBRE_COLOR[c]}
                data-color={c}
                onClick={() => {
                  setValores((a) => ({ ...a, color: c }))
                }}
                className={cn(
                  'flex size-11 items-center justify-center rounded-full text-white transition-transform',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta',
                  elegido && 'ring-2 ring-tinta ring-offset-2 ring-offset-tarjeta',
                )}
                style={{ backgroundColor: `var(--color-pilar-${String(c)})` }}
              >
                {elegido && <CheckIcon className="size-5" strokeWidth={3} aria-hidden />}
              </button>
            )
          })}
        </div>
      </fieldset>

      <Campo
        etiqueta="Descripción corta"
        error={form.errores.descripcion}
        extremo={`${String(descripcion.length)} / 140`}
      >
        {(p) => (
          <AreaTexto
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={descripcion}
            onChange={(e) => {
              setValores((a) => ({ ...a, descripcion: e.target.value }))
            }}
            maxLength={140}
            rows={3}
          />
        )}
      </Campo>
    </ModalMarco>
  )
}

const vacio = (proyectoId: string): PilarEntrada => ({
  proyectoId,
  nombre: '',
  color: 1,
  descripcion: '',
})

const desde = (p: PilarAdmin): PilarEntrada => ({
  proyectoId: p.proyectoId,
  nombre: p.nombre,
  color: p.color,
  descripcion: p.descripcion ?? '',
})
